/* eslint-disable  @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment */
import { Signal } from 'typed-signals';
import type { Popup, PopupConstructor, PopupLooseConstructor } from './Popup';
import { repeatUntilFalse } from '../utils/async-utils';
import { GAME_OBJECT_POOL } from '../game/GameConstants';
import type { PopupStackView } from './PopupStackView';
import { animateInPopup, animateOutPopup, killPopupAnimations } from './PopupAnimations';
import type { App } from '../app/App';
import { removeElement } from '../utils/array-utils';
import { DioramaFillPopup } from '../game/views/popups/winFlow/DioramaFillPopup';
import { DioramaSharePopup } from '../game/views/popups/winFlow/DioramaSharePopup';
import { SettingsPopup } from '../game/views/popups/settings/SettingsPopups';
import { OutOfLivesPopup } from '../game/views/popups/failFlow/OutOfLivesPopup';

type ShowOptionsCommon = {
  data?: any;
  settings?: {
    overlay?: boolean;
    hideRays?: boolean;
    clearStack?: boolean;
    overlayFillOpacity?: number;
  };
  onEvent?: (event: string) => void;
  onAdded?: (popup: Popup) => void;
  onClosed?: (popup: Popup) => void;
};

interface ShowConstructorOptions extends ShowOptionsCommon {
  popupConstructor: PopupConstructor;
}

interface ShowInstanceOptions extends ShowOptionsCommon {
  popupInstance: Popup;
}

type ShowOptions = ShowInstanceOptions | ShowConstructorOptions;

export interface PopupStackItem {
  popup: Popup;
  options: ShowOptions;
}

const notPoolable: PopupConstructor[] = [DioramaFillPopup, DioramaSharePopup, SettingsPopup, OutOfLivesPopup];

export class PopupStackController {
  public readonly stack: PopupStackItem[] = [];

  /**
   * Signal emitted when a popup is added to the stack
   * Useful for reacting to popup open events (e.g., hiding banner ads)
   */
  public readonly onPopupAdded = new Signal<(popup: Popup) => void>();

  /**
   * Signal emitted when a popup is removed from the stack
   * Useful for reacting to popup close events (e.g., showing banner ads)
   */
  public readonly onPopupRemoved = new Signal<(popup: Popup) => void>();

  private getPopupInstance(options: ShowInstanceOptions | ShowConstructorOptions): Popup {
    if ('popupConstructor' in options) {
      const { popupConstructor: constructor } = options;
      if (notPoolable.includes(constructor)) {
        return new constructor(this.app);
      }

      return GAME_OBJECT_POOL.getObject(constructor) ?? new constructor(this.app);
    }

    return options.popupInstance;
  }

  constructor(
    private readonly app: App,
    public readonly view: PopupStackView,
  ) {
    view.onResized.connect(this.alignPopups.bind(this));
    view.resize(app.renderer.width, app.renderer.height);
  }

  /**
   * Checks if there is an active popup of the specified type in the stack.
   *
   * @param popupType - The constructor of the popup type to check for.
   * @returns `true` if there is an active popup of the specified type, otherwise `false`.
   */
  public hasActivePopup(popupType: PopupLooseConstructor): boolean {
    return this.stack.some((item) => item.popup instanceof popupType);
  }

  public async addPopup(options: ShowInstanceOptions | ShowConstructorOptions): Promise<void> {
    const { data, settings, onAdded } = options;
    const prevItem = this.stack.at(-1);

    if (prevItem) {
      if (!settings?.overlay && !settings?.clearStack) await this.hidePopup(prevItem);
      else prevItem.popup.root.interactiveChildren = false;
    }

    const popup = this.getPopupInstance(options);
    const activeItem: PopupStackItem = { popup, options };
    this.stack.push(activeItem);
    popup.onAdded?.(data);
    onAdded?.(popup);

    // Emit signal when popup is added
    this.onPopupAdded.emit(popup);

    this.showPopup(activeItem);
  }

  /**
   * Close the active popup and run the hide animation.
   * @param clearStack option to close all / empty the stack.
   */
  public async closeActivePopup(clearStack = false): Promise<void> {
    const activeItem = this.stack.at(-1);
    if (!activeItem) return;

    if (clearStack) this.closeAllPopups([activeItem.popup]);

    await this.hidePopup(activeItem);
    this.stack.pop(); // Remove popup from stack once hidden.

    const { onClosed } = activeItem.options;
    onClosed?.(activeItem.popup);
    
    // Emit signal when popup is removed (before removing item)
    this.onPopupRemoved.emit(activeItem.popup);
    
    this.removeItem(activeItem);

    const isActivePopupOverlay = !!activeItem.options.settings?.overlay;
    const prevPopup = this.stack.at(-1);

    // call show popup on the previous popup if the active popup was no overlay
    if (prevPopup && !isActivePopupOverlay) this.showPopup(prevPopup);
  }

  /**
   * Does not animate, instant clear.
   * @param exclude
   */
  public closeAllPopups(exclude?: Popup[]): void {
    for (let i = this.stack.length - 1; i >= 0; i -= 1) {
      const item = this.stack[i];
      const { popup } = item;
      if (exclude?.includes(popup)) continue;
      const { onClosed } = item.options;
      onClosed?.(item.popup);

      // Emit signal when popup is removed
      this.onPopupRemoved.emit(popup);

      this.removeItem(this.stack[i]);
    }
    if (this.stack.length === 0) this.view.fadeOutBackgroundElements();

    this.view.pixi.stage.interactiveChildren = true;
  }

  private removeItem(item: PopupStackItem, destroy = false): void {
    const { popup } = item;
    let constructor: PopupConstructor | null = null;

    if ('popupConstructor' in item.options) {
      constructor = item.options.popupConstructor;
    }

    removeElement(this.stack, item);

    popup.root.interactiveChildren = false;
    popup.onEvent?.disconnectAll();

    popup.root.parent?.removeChild(popup.root);
    killPopupAnimations(popup);

    const shouldReturnToPool = !destroy && constructor && !notPoolable.includes(constructor);

    if (shouldReturnToPool && constructor) {
      GAME_OBJECT_POOL.addObject(constructor, popup);
    } else {
      item.popup.root.destroy({ children: true });
    }
  }

  public removeInactivePopupByType(popupType: PopupConstructor, destroy = false): void {
    const activeItem = this.stack.at(-1);
    const items = this.stack.filter((item) => item.popup instanceof popupType && item !== activeItem);

    items.forEach((item) => {
      this.removeItem(item, destroy);
    });
  }

  private showPopup(item: PopupStackItem, animateIn = true): void {
    const { view } = this;
    const { popupContainer } = view;
    const { popup, options } = item;
    const { settings, onEvent } = options;

    popupContainer.addChild(popup.root);

    this.alignPopups();

    if (animateIn) {
      this.view.pixi.stage.interactiveChildren = false;
      const isOverlay = !!item.options.settings?.overlay;
      if (!isOverlay) view.fadeInBackgroundElements(!!settings?.hideRays, settings?.overlayFillOpacity);
      void animateInPopup(popup).then(() => {
        this.view.pixi.stage.interactiveChildren = true;
      });
    }

    if (settings?.clearStack) this.closeAllPopups([popup]);

    popup.root.interactiveChildren = true;
    popup.onShown?.();
    popup.onEvent?.connect((event: string) => {
      onEvent?.(event);
    });
  }

  private async hidePopup({ popup }: PopupStackItem): Promise<void> {
    popup.onEvent?.disconnectAll();
    popup.root.interactiveChildren = false;

    await animateOutPopup(popup, () => {
      if (this.stack.length > 1) return;
      const { view } = this;
      view.fadeOutBackgroundElements();
    });

    popup.onHidden?.();

    popup.root.parent?.removeChild(popup.root);
  }

  public update(deltaTime: number): void {
    this.view.update(deltaTime);
    this.stack.at(-1)?.popup?.update?.(deltaTime);
  }

  public isAnyPopupOpened(): boolean {
    return this.stack.length > 0;
  }

  public async waitUntilNoActivePopups(): Promise<void> {
    await repeatUntilFalse(() => this.stack.length > 0);
  }

  private alignPopups(): void {
    this.view.alignPopups(this.stack.map(({ popup }) => popup));
  }
}
